import React from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, MessageCircle, Truck } from "lucide-react";
import { money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ProductBadges } from "@/components/ProductBadges";
import { toast } from "sonner";

export const ProductCard = ({ product }) => {
  const { addItem } = useCart();
  const soldOut = product.sold_out === true;
  const purchasable = !product.showcase && product.price != null && !soldOut;
  const img = product.images?.[0];

  const handleAdd = (e) => {
    e.preventDefault();
    addItem({
      product_id: product.id,
      name: product.name,
      price: product.price,
      image: img,
    });
    toast.success(`${product.name} added to cart`);
  };

  return (
    <Link
      to={`/product/${product.slug}`}
      data-testid={`product-card-${product.slug}`}
      className="group flex flex-col bg-white rounded-lg border border-navy/15 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
    >
      <div className="relative h-56 overflow-hidden bg-neutral-100">
        {img ? (
          <img
            src={img}
            alt={product.name}
            loading="lazy"
            className={`w-full h-full object-contain object-center transition-transform duration-500 group-hover:scale-105 ${soldOut ? "opacity-60" : ""}`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-navy/30 font-display text-2xl">JM</div>
        )}
        <span className="absolute top-3 left-3 bg-cream/95 text-navy text-[11px] font-bold rounded-full px-3 py-1">
          {product.category}
        </span>
        {soldOut && (
          <span data-testid={`sold-out-badge-${product.slug}`} className="absolute top-3 right-3 bg-brick text-white text-[11px] font-bold rounded-full px-3 py-1 uppercase tracking-wide">
            Sold Out
          </span>
        )}
        {product.is_dropship && (
          <span
            data-testid={`supplier-badge-${product.slug}`}
            className="absolute bottom-3 left-3 inline-flex items-center gap-1 bg-navy/90 text-cream text-[10px] font-bold rounded-full px-2.5 py-1 backdrop-blur-sm"
          >
            <Truck className="w-3 h-3" /> Ships from supplier
          </span>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        {product.badges?.length > 0 && (
          <div className="mb-2">
            <ProductBadges badges={product.badges} slug={product.slug} />
          </div>
        )}
        <h3 className="font-display text-xl text-navy leading-tight">{product.name}</h3>
        {product.condition && (
          <span data-testid={`condition-${product.slug}`} className="mt-1 text-xs font-semibold text-navy/60 uppercase tracking-wide">{product.condition}</span>
        )}
        <p className="mt-2 text-sm text-ink/70 line-clamp-2 flex-1">{product.description}</p>
        <div className="mt-4 flex items-center justify-between gap-2">
          <span className="font-display text-xl text-brick">
            {product.showcase || product.price == null ? product.price_label : money(product.price)}
          </span>
          {soldOut ? (
            <span className="inline-flex items-center gap-1.5 text-brick text-sm font-bold uppercase">Sold Out</span>
          ) : purchasable ? (
            <button
              data-testid={`add-to-cart-${product.slug}`}
              onClick={handleAdd}
              className="inline-flex items-center gap-1.5 bg-brick text-white text-sm font-bold uppercase rounded-md px-3 py-2 hover:bg-navy transition-colors"
            >
              <ShoppingCart className="w-4 h-4" /> Add
            </button>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-navy text-sm font-bold uppercase">
              <MessageCircle className="w-4 h-4" /> Inquire
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};
